import { collectLayoutReferencedQids } from '../../../components/oasis/bespoke/layout/collectLayoutReferencedQids';
import { collectLayoutRenderableFieldQids } from '../../../components/oasis/bespoke/layout/collectLayoutRenderableFieldQids';
import {
  getLayoutStructureChildLists,
  mapLayoutStructureChildren,
} from '../../../components/oasis/bespoke/layout/layoutStructureChildLists';
import type { LayoutNode } from '../../../components/oasis/bespoke/layout/types';
import {
  addConditionalClauseRefQidsToSet,
  addWhenGateRefQidsToSet,
} from '../../../components/oasis/bespoke/layout/whenClause';

/**
 * Source-supreme section-port splice (EMR-167 / EMR-554).
 *
 * Design: rather than mutating the destination's tree field-by-field (which
 * cannot distinguish "this field's destination position is still correct"
 * from "source has since corrected this field's gating/position" — see git
 * history for the abandoned anchor-preserving approach), the result is
 * *rebuilt* from source:
 *
 * 1. `buildSharedSkeleton` prunes source-only content out of the *current
 *    source layout*, keeping everything else (order, gating, wrappers)
 *    verbatim. This is the canonical arrangement for every shared QID —
 *    identical for every destination in the same schema-signature group,
 *    which is what makes "similar sections end up looking the same" actually
 *    true, and what makes a second splice run a structural fixed point. A
 *    `conditional` / `disabled-when` wrapper whose *own* gate references a
 *    QID pruned as source-only is unwrapped rather than kept dangling —
 *    its condition can no longer be evaluated once that QID is gone.
 * 2. `extractDestOnlyBlocks` pulls destination-only content (plus any
 *    schema-shared QID that source's *current* layout doesn't happen to
 *    render — defensive, should not occur for a valid source layout) out of
 *    the *original* destination tree, recording its real prior adjacency to
 *    stable shared content (not a guessed schema-order neighbor).
 * 3. The dest-only blocks are re-threaded into the skeleton at that same
 *    adjacency, so genuinely destination-specific content survives, while
 *    every QID source and destination share is arranged exactly as source
 *    arranges it — including gating. A field source moved out of a
 *    conditional therefore always ends up ungated in the result, regardless
 *    of where it used to live in the destination.
 */
export type SectionPortSpliceInput = {
  sourceLayout: LayoutNode[];
  destLayout: LayoutNode[];
  sharedQids: string[];
  sourceOnlyQids: string[];
  destOnlyQids: string[];
};

/** Path segment under a parent: structural child-list index + index in that list. */
type PathSeg = { list: number; index: number };
/** Root segment uses `list: 0`; deeper segments use the parent's child-list index. */
type NodePath = PathSeg[];

function toSet(values: string[]): Set<string> {
  return new Set(values);
}

function renderableQids(node: LayoutNode): Set<string> {
  return collectLayoutRenderableFieldQids([node]);
}

function referencedQids(node: LayoutNode): Set<string> {
  return collectLayoutReferencedQids([node]);
}

function cloneNode<T>(node: T): T {
  return structuredClone(node);
}

function pathKey(path: NodePath): string {
  return path.map(s => `${s.list}:${s.index}`).join('/');
}

function isPathPrefix(prefix: NodePath, full: NodePath): boolean {
  if (prefix.length >= full.length) return false;
  return prefix.every(
    (seg, i) => seg.list === full[i].list && seg.index === full[i].index
  );
}

/**
 * True when every renderable + gate/ref QID in the subtree is within
 * `allowedRenderable`. `allowedGate` (defaults to `allowedRenderable`) covers
 * referenced/gate QIDs separately — a dest-only block may be gated on a
 * *shared* condition without that condition itself being dest-only.
 * Requires at least one renderable field (pure chrome is never a donor root).
 */
export function nodePortableForAllowedQids(
  node: LayoutNode,
  allowedRenderable: Set<string>,
  allowedGate: Set<string> = allowedRenderable
): boolean {
  const renderable = renderableQids(node);
  if (renderable.size === 0) return false;
  for (const qid of renderable) {
    if (!allowedRenderable.has(qid)) return false;
  }
  for (const qid of referencedQids(node)) {
    if (!allowedGate.has(qid)) return false;
  }
  return true;
}

/**
 * Resolve path: first segment indexes the root array; later segments use
 * `list` + `index` under the parent node via {@link getLayoutStructureChildLists}.
 */
function nodeAtPathFromRoot(
  nodes: LayoutNode[],
  path: NodePath
): LayoutNode | null {
  if (path.length === 0) return null;
  let node: LayoutNode | null = null;
  for (let depth = 0; depth < path.length; depth++) {
    const seg = path[depth];
    if (depth === 0) {
      if (seg.index < 0 || seg.index >= nodes.length) return null;
      node = nodes[seg.index];
      continue;
    }
    if (node == null) return null;
    const childLists = getLayoutStructureChildLists(node);
    const childList = childLists[seg.list];
    if (!childList || seg.index < 0 || seg.index >= childList.length) {
      return null;
    }
    node = childList[seg.index];
  }
  return node;
}

/** Deepest path to a renderable control for `qid`, or null. */
function findDeepestPathWithRenderableQid(
  nodes: LayoutNode[],
  qid: string
): NodePath | null {
  for (let index = 0; index < nodes.length; index++) {
    const found = findInNode(nodes[index], qid, [{ list: 0, index }]);
    if (found) return found;
  }
  return null;
}

function findInNode(
  node: LayoutNode,
  qid: string,
  pathToNode: NodePath
): NodePath | null {
  if (!renderableQids(node).has(qid)) return null;

  const childLists = getLayoutStructureChildLists(node);
  for (let list = 0; list < childLists.length; list++) {
    const children = childLists[list];
    for (let index = 0; index < children.length; index++) {
      const found = findInNode(children[index], qid, [
        ...pathToNode,
        { list, index },
      ]);
      if (found) return found;
    }
  }
  return pathToNode;
}

/**
 * QIDs a node's *own* gate condition references — not its descendants'. Only
 * `conditional` and `disabled-when` carry an own gate; every other node type
 * returns an empty set. Used by {@link pruneQidsFromNode} to detect a
 * wrapper whose condition itself points at a QID being pruned (e.g. a
 * source-only gate field) — see EMR-167 dangling-gate fix. Table `when` /
 * `whenAll` row gates are a separate per-row concept (not a `LayoutNode`
 * type) and are not covered here.
 *
 * TODO(EMR-167): known gap, not fixed — if a source-only gate question is
 * pruned, a table row can keep a `when` / `whenAll` condition that points at
 * that removed QID, so shared fields in the row may stay hidden in the
 * destination after porting (same dangling-gate failure mode as the
 * `conditional` / `disabled-when` case above, just unhandled for table
 * rows). Fix would mirror the wrapper-unwrap behavior: clear the row's gate
 * (ship the row ungated) when its condition refs a pruned QID.
 */
function nodeOwnGateQids(node: LayoutNode): Set<string> {
  const ids = new Set<string>();
  if (node.type === 'conditional') {
    addWhenGateRefQidsToSet(ids, node);
  } else if (node.type === 'disabled-when') {
    addConditionalClauseRefQidsToSet(ids, node.whenAll);
  }
  return ids;
}

function isRenderableFieldNode(node: LayoutNode): boolean {
  return (
    node.type === 'OASISRadio' ||
    node.type === 'OASISYesNoCheckbox' ||
    node.type === 'OASISSelect' ||
    node.type === 'OASISText' ||
    node.type === 'OASISDate' ||
    node.type === 'OASISTime' ||
    node.type === 'OASISNumber' ||
    node.type === 'radio-option' ||
    node.type === 'OASISMultiselect'
  );
}

/**
 * Remove every node whose entire renderable QID set is within `qidsToPrune`;
 * partially-matching containers are rebuilt (recursing into children) and
 * kept, so unrelated content in the same wrapper survives untouched. Used to
 * build the shared skeleton (pruning source-only QIDs out of source).
 */
function pruneQidsFromLayout(
  nodes: LayoutNode[],
  qidsToPrune: Set<string>
): LayoutNode[] {
  const out: LayoutNode[] = [];
  for (const node of nodes) {
    const pruned = pruneQidsFromNode(node, qidsToPrune);
    if (pruned == null) continue;
    if (Array.isArray(pruned)) {
      out.push(...pruned);
    } else {
      out.push(pruned);
    }
  }
  return out;
}

function pruneQidsFromNode(
  node: LayoutNode,
  qidsToPrune: Set<string>
): LayoutNode | LayoutNode[] | null {
  const nodeQids = renderableQids(node);
  const hitsPruneSet = [...nodeQids].some(qid => qidsToPrune.has(qid));
  // A wrapper's own gate can reference a QID being pruned even when none of
  // its *rendered* descendants do (e.g. a `disabled-when` gated on a
  // source-only question that wraps only shared fields) — that condition
  // can no longer be evaluated once the gate QID is gone, so it must trigger
  // the same recursion path as a renderable hit rather than being skipped.
  const ownGateQids = nodeOwnGateQids(node);
  const gateHitsPruneSet = [...ownGateQids].some(qid => qidsToPrune.has(qid));
  // TODO(EMR-167): known gap, not fixed — when neither this node's renderable
  // QIDs nor its *own* gate hit the prune set, we return without descending.
  // A nested `conditional` / `disabled-when` inside a `stack` / `card` can
  // still gate on a source-only QID (often a root sibling outside the
  // container), leaving a dangling `when` / `whenAll` in the skeleton. Fix:
  // recurse into structural children before this fast path (or detect
  // descendant own-gate hits) and unwrap nested wrappers the same way we do
  // at the root — add a nested-wrapper regression test when implementing.
  if (!hitsPruneSet && !gateHitsPruneSet) return node;

  if (nodeQids.size > 0 && [...nodeQids].every(qid => qidsToPrune.has(qid))) {
    return null;
  }

  if (
    isRenderableFieldNode(node) &&
    'qid' in node &&
    qidsToPrune.has(node.qid)
  ) {
    return null;
  }

  const childLists = getLayoutStructureChildLists(node);
  if (childLists.length === 0) return node;

  const rebuilt = mapLayoutStructureChildren(node, children =>
    pruneQidsFromLayout(children, qidsToPrune)
  );

  if (gateHitsPruneSet) {
    // The node's own gate now points at a pruned (source-only) QID, so the
    // condition is meaningless in the result — unwrap the wrapper instead of
    // leaving a dangling reference: splice its (already-pruned) primary
    // children into the parent list, ungated. For `conditional`, this keeps
    // the `then` branch and drops `elseChildren`, since there's no way to
    // know which branch the destination should show once the gate itself is
    // gone.
    return getLayoutStructureChildLists(rebuilt)[0] ?? [];
  }

  const stillHasRenderable = renderableQids(rebuilt).size > 0;
  const isStructuralChrome =
    rebuilt.type === 'static' ||
    rebuilt.type === 'text' ||
    rebuilt.type === 'section-label' ||
    rebuilt.type === 'divider' ||
    rebuilt.type === 'display';
  if (!stillHasRenderable && !isStructuralChrome) {
    const hasChildren = getLayoutStructureChildLists(rebuilt).some(
      list => list.length > 0
    );
    if (!hasChildren) return null;
  }
  return rebuilt;
}

/**
 * Source's arrangement of shared-only content, verbatim (order, gating,
 * chrome). This is the canonical shape every destination in the same
 * schema-signature group converges to.
 */
export function buildSharedSkeleton(
  sourceLayout: LayoutNode[],
  sourceOnlyQids: Set<string>
): LayoutNode[] {
  return pruneQidsFromLayout(sourceLayout, sourceOnlyQids);
}

type DestOnlyAnchor =
  | {
      kind: 'sibling';
      direction: 'after' | 'before';
      /**
       * Every stable shared QID the matched sibling renders — not just one
       * representative. The matched sibling may have been a whole group
       * (e.g. a card with several shared fields); if source has since
       * scattered that group's members into different parents, resolving
       * against a *single* arbitrarily-chosen member can land the dest-only
       * block between two group members instead of after/before all of
       * them. Keeping every member lets {@link resolveSkeletonInsertPath}
       * pick, at resolve time, whichever member now renders last (`after`)
       * or first (`before`) in the *current* skeleton.
       */
      qids: string[];
      /** Length of the matched sibling's own path — used to re-locate the
       *  equivalent *sibling-level* position in the skeleton, not whatever
       *  depth the matched QID itself happens to sit at within that sibling. */
      siblingPathLength: number;
    }
  | { kind: 'root' };

export type DestOnlyMergeBlock = {
  node: LayoutNode;
  anchor: DestOnlyAnchor;
};

function childListAtParentPath(
  nodes: LayoutNode[],
  parentPath: NodePath,
  childListIndex: number
): LayoutNode[] | null {
  if (parentPath.length === 0) return nodes;
  const parent = nodeAtPathFromRoot(nodes, parentPath);
  if (!parent) return null;
  const lists = getLayoutStructureChildLists(parent);
  return lists[childListIndex] ?? null;
}

function siblingListAtPath(
  tree: LayoutNode[],
  path: NodePath
): { list: LayoutNode[]; index: number } | null {
  if (path.length === 0) return null;
  const parentPath = path.slice(0, -1);
  const last = path[path.length - 1];
  const list =
    parentPath.length === 0
      ? tree
      : childListAtParentPath(tree, parentPath, last.list);
  if (!list) return null;
  return { list, index: last.index };
}

/** Every QID (deterministic order) of `node`'s renderable set that is in `allowed`. */
function allowedQidsIn(node: LayoutNode, allowed: Set<string>): string[] {
  return [...renderableQids(node)]
    .filter(q => allowed.has(q))
    .sort((a, b) => a.localeCompare(b));
}

/**
 * Walk outward from the dest-only block's own level until a sibling with a
 * *stable* shared QID (one that will actually exist in the skeleton — see
 * `stableSharedQids` in {@link spliceSectionPortLayout}) is found, preferring
 * the nearest preceding sibling, then the nearest following one. Records the
 * matched sibling's own path length so the merge step can re-locate the
 * equivalent *sibling-level* slot in the skeleton, rather than nesting inside
 * whatever the matched QID's own (possibly deeper) position happens to be.
 */
function findDestOnlyAnchor(
  destLayout: LayoutNode[],
  blockPath: NodePath,
  stableSharedQids: Set<string>
): DestOnlyAnchor {
  for (let depth = blockPath.length; depth >= 1; depth--) {
    const pathAtDepth = blockPath.slice(0, depth);
    const info = siblingListAtPath(destLayout, pathAtDepth);
    if (!info) continue;
    const { list, index } = info;
    for (let i = index - 1; i >= 0; i--) {
      const qids = allowedQidsIn(list[i], stableSharedQids);
      if (qids.length > 0) {
        return {
          kind: 'sibling',
          direction: 'after',
          qids,
          siblingPathLength: depth,
        };
      }
    }
    for (let i = index + 1; i < list.length; i++) {
      const qids = allowedQidsIn(list[i], stableSharedQids);
      if (qids.length > 0) {
        return {
          kind: 'sibling',
          direction: 'before',
          qids,
          siblingPathLength: depth,
        };
      }
    }
  }
  return { kind: 'root' };
}

/**
 * Maximal portable dest-only blocks: bottom-up, for each dest-only-eligible
 * QID take the most senior portable ancestor in the destination tree and
 * collapse overlapping QIDs into unique maximal blocks (destination order),
 * each paired with where it was actually adjacent to stable shared content
 * in the *original* destination.
 *
 * TODO(EMR-167): known gap, not fixed — ascent stops when a parent is
 * non-portable because it still renders shared QIDs (e.g. destination
 * `conditional(sharedGate) -> [shared a, dest-only b]`). Extraction keeps
 * bare `b`; if source later moves `a` out of that gate, merge re-threads
 * `b` beside ungated `a` and `b` loses the destination's gating. Fix:
 * when ascent breaks, clone the nearest gating ancestor with shared
 * renderable descendants pruned out (e.g. `conditional(sharedGate) -> [b]`)
 * so dest-only fields keep their prior visibility rules.
 */
export function extractDestOnlyBlocks(
  destLayout: LayoutNode[],
  allowedQids: Set<string>,
  stableSharedQids: Set<string>
): DestOnlyMergeBlock[] {
  const gateAllowed = new Set<string>([...allowedQids, ...stableSharedQids]);
  const isPortable = (node: LayoutNode): boolean =>
    nodePortableForAllowedQids(node, allowedQids, gateAllowed);

  const seedQids = [...allowedQids].sort((a, b) => a.localeCompare(b));
  const candidatePaths: NodePath[] = [];
  for (const qid of seedQids) {
    const leafPath = findDeepestPathWithRenderableQid(destLayout, qid);
    if (!leafPath) continue;
    const leaf = nodeAtPathFromRoot(destLayout, leafPath);
    if (!leaf || !isPortable(leaf)) continue;

    let bestPath = leafPath;
    for (let len = leafPath.length - 1; len >= 1; len--) {
      const parentPath = leafPath.slice(0, len);
      const parent = nodeAtPathFromRoot(destLayout, parentPath);
      if (parent && isPortable(parent)) {
        bestPath = parentPath;
      } else {
        break;
      }
    }
    candidatePaths.push(bestPath);
  }

  const uniqueKeys = new Map<string, NodePath>();
  for (const path of candidatePaths) uniqueKeys.set(pathKey(path), path);

  const maximalPaths: NodePath[] = [];
  for (const path of uniqueKeys.values()) {
    const dominated = [...uniqueKeys.values()].some(other =>
      isPathPrefix(other, path)
    );
    if (!dominated) maximalPaths.push(path);
  }
  maximalPaths.sort(comparePathsPreOrder);

  return maximalPaths.map(path => ({
    node: cloneNode(nodeAtPathFromRoot(destLayout, path)!),
    anchor: findDestOnlyAnchor(destLayout, path, stableSharedQids),
  }));
}

/** Sibling segment immediately after `path`'s last segment (same list). */
function pathAfterSibling(path: NodePath): NodePath {
  if (path.length === 0) return path;
  const out = path.map(seg => ({ ...seg }));
  out[out.length - 1] = {
    ...out[out.length - 1],
    index: out[out.length - 1].index + 1,
  };
  return out;
}

function insertUnderNode(
  node: LayoutNode,
  path: NodePath,
  newNode: LayoutNode
): LayoutNode {
  if (path.length === 0) return node;
  const [seg, ...rest] = path;

  if (rest.length === 0) {
    let listCounter = 0;
    return mapLayoutStructureChildren(node, children => {
      const currentList = listCounter++;
      if (currentList !== seg.list) return children;
      const out = children.slice();
      out.splice(seg.index, 0, newNode);
      return out;
    });
  }

  let listCounter = 0;
  return mapLayoutStructureChildren(node, children => {
    const currentList = listCounter++;
    if (currentList !== seg.list) return children;
    return children.map((child, idx) => {
      if (idx !== seg.index) return child;
      return insertUnderNode(child, rest, newNode);
    });
  });
}

function insertNodeAtPath(
  nodes: LayoutNode[],
  path: NodePath,
  newNode: LayoutNode
): LayoutNode[] {
  if (path.length === 0) return [...nodes, newNode];

  if (path.length === 1) {
    const out = nodes.slice();
    out.splice(path[0].index, 0, newNode);
    return out;
  }

  const rootIndex = path[0].index;
  const rest = path.slice(1);
  return nodes.map((node, i) => {
    if (i !== rootIndex) return node;
    return insertUnderNode(node, rest, newNode);
  });
}

/**
 * Pre-order comparison of two node paths, using the same (list, index)
 * traversal order as {@link findInNode} / {@link getLayoutStructureChildLists}.
 * Negative when `a` renders before `b`; an ancestor path sorts before any of
 * its descendants.
 */
function comparePathsPreOrder(a: NodePath, b: NodePath): number {
  const len = Math.min(a.length, b.length);
  for (let i = 0; i < len; i++) {
    if (a[i].list !== b[i].list) return a[i].list - b[i].list;
    if (a[i].index !== b[i].index) return a[i].index - b[i].index;
  }
  return a.length - b.length;
}

/**
 * Locate the equivalent sibling-level slot for `anchor` inside `skeleton`.
 * The matched sibling may have rendered several stable shared QIDs (a whole
 * group, not one field) — find each one's current path in `skeleton` and
 * pick whichever now renders *last* (`after`) or *first* (`before`), so the
 * dest-only block lands after/before the *whole* group even if source has
 * since scattered its members into different parents. Then truncate that
 * one path to the matched sibling's original path length — re-locating "the
 * sibling", not "wherever inside the sibling this particular QID renders".
 */
function resolveSkeletonInsertPath(
  skeleton: LayoutNode[],
  anchor: DestOnlyAnchor
): NodePath | null {
  if (anchor.kind === 'root') return null;
  const qidPaths = anchor.qids
    .map(qid => findDeepestPathWithRenderableQid(skeleton, qid))
    .filter((path): path is NodePath => path != null);
  if (qidPaths.length === 0) return null;
  const extremePath = qidPaths.reduce((best, path) =>
    (
      anchor.direction === 'after'
        ? comparePathsPreOrder(path, best) > 0
        : comparePathsPreOrder(path, best) < 0
    )
      ? path
      : best
  );
  const truncated = extremePath.slice(
    0,
    Math.min(anchor.siblingPathLength, extremePath.length)
  );
  if (truncated.length === 0) return null;
  return anchor.direction === 'after' ? pathAfterSibling(truncated) : truncated;
}

/**
 * Re-thread dest-only blocks into the skeleton at their recorded adjacency.
 * Blocks sharing an anchor are grouped and inserted together so their
 * original relative order survives: "after X" groups insert in reverse
 * (each lands immediately after X, pushing earlier-processed ones further
 * along); "before X" groups insert forward (each lands immediately before X,
 * pushing X and later-processed ones further along). Root-level (no anchor
 * found) blocks are appended in original order.
 */
function mergeDestOnlyBlocksIntoSkeleton(
  skeleton: LayoutNode[],
  blocks: DestOnlyMergeBlock[]
): LayoutNode[] {
  const order: string[] = [];
  const groups = new Map<
    string,
    { anchor: DestOnlyAnchor; nodes: LayoutNode[] }
  >();

  for (const block of blocks) {
    // `siblingPathLength` is part of the key, not just `direction:qid`: two
    // dest-only blocks can walk outward to the *same* stable shared QID from
    // different original depths (e.g. one sat beside a field inside a card,
    // another beside that whole card at the section root) — collapsing them
    // into one group would force both to reuse whichever block's depth
    // happened to create the group, pulling the shallower block's content
    // into the deeper block's wrapper (or vice versa).
    const key =
      block.anchor.kind === 'root'
        ? 'root'
        : `${block.anchor.direction}:${block.anchor.qids.join(',')}:${block.anchor.siblingPathLength}`;
    let group = groups.get(key);
    if (!group) {
      group = { anchor: block.anchor, nodes: [] };
      groups.set(key, group);
      order.push(key);
    }
    group.nodes.push(block.node);
  }

  let result = skeleton;
  for (const key of order) {
    const group = groups.get(key)!;
    const nodesInInsertOrder =
      group.anchor.kind === 'sibling' && group.anchor.direction === 'after'
        ? [...group.nodes].reverse()
        : group.nodes;
    for (const node of nodesInInsertOrder) {
      const insertPath = resolveSkeletonInsertPath(result, group.anchor);
      result =
        insertPath != null
          ? insertNodeAtPath(result, insertPath, node)
          : [...result, node];
    }
  }
  return result;
}

/**
 * Rebuild the destination section from source's arrangement of shared
 * content (see module doc), re-threading destination-only content (plus any
 * schema-shared QID source's current layout doesn't render — defensive) at
 * its real prior adjacency to stable shared content.
 */
export function spliceSectionPortLayout(
  input: SectionPortSpliceInput
): LayoutNode[] {
  const shared = toSet(input.sharedQids);
  const sourceOnly = toSet(input.sourceOnlyQids);
  const destOnly = toSet(input.destOnlyQids);

  const skeleton = buildSharedSkeleton(input.sourceLayout, sourceOnly);

  const missingFromSkeleton = new Set<string>();
  for (const qid of shared) {
    if (!findDeepestPathWithRenderableQid(skeleton, qid)) {
      missingFromSkeleton.add(qid);
    }
  }

  const preserveFromDest = new Set<string>([
    ...destOnly,
    ...missingFromSkeleton,
  ]);
  if (preserveFromDest.size === 0) {
    return skeleton;
  }

  // Only anchor to QIDs that will actually exist in the skeleton — anchoring
  // to another preserve-from-dest QID (itself not in the skeleton) would
  // resolve to nothing and fall back to root, losing relative order.
  const stableShared = new Set(
    [...shared].filter(qid => !missingFromSkeleton.has(qid))
  );

  const blocks = extractDestOnlyBlocks(
    input.destLayout,
    preserveFromDest,
    stableShared
  );
  if (blocks.length === 0) {
    return skeleton;
  }

  return mergeDestOnlyBlocksIntoSkeleton(skeleton, blocks);
}
